import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProfile } from '@/hooks/useProfile';
import type { AttendeeFields, Event, PaginatedEvent } from '@mr-website/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  GuestSection,
  removeGuestById,
  syncGuestsWithCount,
  validateGuests,
  type GuestRow,
} from './GuestSection';
import {
  SpouseSection,
  createDefaultSpouseFields,
  createInitialSpouseState,
  validateSpouse,
  type SpouseFormState,
} from './SpouseSection';
import { createRsvpFormState } from './buildRsvpFormState';
import { cancelRsvp } from './cancelRsvp';
import { submitRsvp } from './submitRsvp';
import type { LinkedSpouse } from './types';
import { attendeeFieldErrorFor, resolveLinkedSpouse, type FieldErrors } from './utils';
import {
  canEnableSpouse,
  EVENT_AT_CAPACITY_LABEL,
  getMaxCompanions,
  getMaxGuests,
  isEventAtCapacity,
  isOverCompanionLimit,
} from './capacity';

type Stage = 'form' | 'success';
type SuccessMode = 'created' | 'updated';

interface RsvpDialogProps {
  event: Event;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isManagingRsvp?: boolean;
}

export function RsvpDialog({ event, open, onOpenChange, isManagingRsvp = false }: RsvpDialogProps) {
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const canBringOthers = !event.is_online;
  const [stage, setStage] = useState<Stage>('form');
  const [successMode, setSuccessMode] = useState<SuccessMode | null>(null);
  const [invitesSent, setInvitesSent] = useState(false);
  const wasOpenRef = useRef(false);
  const [spouse, setSpouse] = useState<SpouseFormState>(() => createInitialSpouseState(null));
  const [guestState, setGuestState] = useState<{ guests: GuestRow[]; guestCount: number }>(
    () => createRsvpFormState(null).guestState,
  );
  const [spouseErrors, setSpouseErrors] = useState<FieldErrors>({});
  const [guestErrors, setGuestErrors] = useState<Record<string, FieldErrors>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const linkedSpouse = useMemo<LinkedSpouse | null>(
    () => resolveLinkedSpouse(profile?.spouse, event.user_rsvp?.spouse),
    [profile?.spouse, event.user_rsvp?.spouse],
  );
  const atCapacity = isEventAtCapacity(event) && !isManagingRsvp;
  const maxCompanions = getMaxCompanions(event, isManagingRsvp);
  const maxGuests = getMaxGuests(maxCompanions, spouse.enabled);
  const spouseCanEnable = canEnableSpouse(maxCompanions, spouse.enabled, guestState.guestCount);

  const submitMutation = useMutation({
    mutationFn: () =>
      submitRsvp({
        eventId: event.id,
        linkedSpouse,
        spouse,
        guests: guestState.guests,
      }),
    onSuccess: ({ createdSpouse }) => {
      if (createdSpouse) {
        void queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
      setSuccessMode(isManagingRsvp ? 'updated' : 'created');
      void queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      setStage('success');
    },
    onError: (error: unknown) => {
      const message =
        (error instanceof Error ? error.message : null) ??
        'Unable to submit your RSVP right now. Please try again.';
      setSubmitError(message);
      toast.error(message);
      setStage('form');
    },
  });

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!justOpened) return;

    setStage('form');
    setSuccessMode(null);

    const initialState =
      isManagingRsvp && event.user_rsvp
        ? createRsvpFormState(linkedSpouse, event.user_rsvp)
        : createRsvpFormState(linkedSpouse);

    setSpouse(initialState.spouse);
    setGuestState(initialState.guestState);
    setSpouseErrors({});
    setGuestErrors({});
    setSubmitError(null);
    setInvitesSent(false);

    // Nothing for the user to fill in for a new RSVP to an online event; submit right away,
    // same as if the user had clicked "Submit RSVP" themselves.
    if (canBringOthers || isManagingRsvp) return;
    submitMutation.mutate();
  }, [open, linkedSpouse, isManagingRsvp, event.user_rsvp, canBringOthers]);

  // Profile can load after the dialog opens; refresh spouse fields once linked data arrives.
  useEffect(() => {
    if (!open || !linkedSpouse?.contact_id) return;

    setSpouse((current) => {
      if (!current.enabled) return current;

      return {
        enabled: true,
        fields: {
          ...createDefaultSpouseFields(linkedSpouse),
          send_invite: current.fields.send_invite,
        },
      };
    });
  }, [open, linkedSpouse?.contact_id, linkedSpouse?.first_name, linkedSpouse?.last_name]);

  const cancelMutation = useMutation({
    mutationFn: () => cancelRsvp(event.id),
    onSuccess: (freshEvent) => {
      queryClient.setQueryData(['event', event.id], freshEvent);
      queryClient.setQueriesData<PaginatedEvent>({ queryKey: ['events'] }, (current) => {
        if (!current?.data) return current;
        return {
          ...current,
          data: current.data.map((item) =>
            item.id === freshEvent.id
              ? {
                  ...item,
                  rsvp_count: freshEvent.rsvp_count,
                  max_capacity: freshEvent.max_capacity,
                  is_full: freshEvent.is_full,
                  spots_remaining: freshEvent.spots_remaining,
                  user_rsvp: freshEvent.user_rsvp,
                }
              : item,
          ),
        };
      });
      toast.success('Your RSVP has been cancelled.');
      onOpenChange(false);
    },
    onError: () => {
      setSubmitError('Unable to cancel your RSVP right now. Please try again.');
      toast.error('Unable to cancel your RSVP right now. Please try again.');
    },
  });

  function handleSpouseChange(next: SpouseFormState) {
    const toggled = next.enabled !== spouse.enabled;
    setSpouse(next);
    if (toggled && next.enabled) {
      const nextMaxGuests = getMaxGuests(maxCompanions, true);
      setGuestState((current) => ({
        guestCount: Math.min(current.guestCount, nextMaxGuests),
        guests: syncGuestsWithCount(current.guests, Math.min(current.guestCount, nextMaxGuests)),
      }));
    }
    setSpouseErrors((current) => {
      if (toggled) return {};
      const changedFields = (Object.keys(next.fields) as (keyof AttendeeFields)[]).filter(
        (field) => next.fields[field] !== spouse.fields[field],
      );
      if (changedFields.length === 0) return current;
      const nextErrors = { ...current };
      for (const field of changedFields) delete nextErrors[field];
      return nextErrors;
    });
  }

  function handleSpouseFieldBlur(field: keyof AttendeeFields, fields: AttendeeFields) {
    if (!spouse.enabled) return;

    const nextSpouseErrors = validateSpouse(linkedSpouse, { enabled: true, fields });
    setSpouseErrors((current) => {
      const next = { ...current };
      const error = nextSpouseErrors[field];
      if (error) {
        next[field] = error;
      } else {
        delete next[field];
      }
      return next;
    });
  }

  function handleGuestFieldBlur(
    guestId: string,
    field: keyof AttendeeFields,
    values: AttendeeFields,
  ) {
    const error = attendeeFieldErrorFor(values, field);
    setGuestErrors((current) => {
      const next = { ...current };
      const guestErrors = { ...(next[guestId] ?? {}) };
      if (error) {
        guestErrors[field] = error;
      } else {
        delete guestErrors[field];
      }
      if (Object.keys(guestErrors).length === 0) {
        delete next[guestId];
      } else {
        next[guestId] = guestErrors;
      }
      return next;
    });
  }

  function handleGuestCountChange(count: number) {
    setGuestState((current) => ({
      guestCount: count,
      guests: syncGuestsWithCount(current.guests, count),
    }));
    setGuestErrors({});
  }

  function handleGuestChange(id: string, next: AttendeeFields) {
    const previous = guestState.guests.find((guest) => guest.id === id);
    setGuestState((current) => ({
      ...current,
      guests: current.guests.map((guest) => (guest.id === id ? { ...next, id } : guest)),
    }));
    setGuestErrors((current) => {
      const guestErrors = current[id];
      if (!guestErrors || !previous) return current;
      const changedFields = (Object.keys(next) as (keyof AttendeeFields)[]).filter(
        (field) => next[field] !== previous[field],
      );
      if (changedFields.length === 0) return current;
      const nextGuestErrors = { ...guestErrors };
      for (const field of changedFields) delete nextGuestErrors[field];
      const nextAll = { ...current };
      if (Object.keys(nextGuestErrors).length === 0) {
        delete nextAll[id];
      } else {
        nextAll[id] = nextGuestErrors;
      }
      return nextAll;
    });
  }
  function handleGuestRemove(id: string) {
    setGuestState((current) => {
      const { guests, count } = removeGuestById(current.guests, id);
      return { guests, guestCount: count };
    });
    setGuestErrors((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  function handleSubmit(eventSubmit: React.FormEvent) {
    eventSubmit.preventDefault();
    setSubmitError(null);

    const nextSpouseErrors = canBringOthers ? validateSpouse(linkedSpouse, spouse) : {};
    const nextGuestErrors = canBringOthers ? validateGuests(guestState.guests) : {};

    setSpouseErrors(nextSpouseErrors);
    setGuestErrors(nextGuestErrors);

    if (Object.keys(nextSpouseErrors).length > 0 || Object.keys(nextGuestErrors).length > 0) {
      return;
    }

    if (atCapacity) {
      setSubmitError(EVENT_AT_CAPACITY_LABEL);
      return;
    }

    if (
      canBringOthers &&
      isOverCompanionLimit(maxCompanions, spouse.enabled, guestState.guestCount)
    ) {
      setSubmitError('There are not enough seats remaining for this many guests.');
      return;
    }

    setInvitesSent(
      canBringOthers &&
        ((spouse.enabled && spouse.fields.send_invite) ||
          guestState.guests.some((guest) => guest.send_invite)),
    );
    submitMutation.mutate();
  }

  function handleCancelRsvp() {
    setSubmitError(null);
    if (
      !window.confirm(
        'Cancel your RSVP for this event? This will remove you and anyone you registered with you.',
      )
    ) {
      return;
    }
    cancelMutation.mutate();
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setStage('form');
      setSuccessMode(null);
      const initialState =
        isManagingRsvp && event.user_rsvp
          ? createRsvpFormState(linkedSpouse, event.user_rsvp)
          : createRsvpFormState(linkedSpouse);
      setSpouse(initialState.spouse);
      setGuestState(initialState.guestState);
      setSpouseErrors({});
      setGuestErrors({});
      setSubmitError(null);
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        showCloseButton={stage === 'form'}
      >
        {stage === 'form' && atCapacity ? (
          <>
            <DialogHeader>
              <DialogTitle>{EVENT_AT_CAPACITY_LABEL}</DialogTitle>
              <DialogDescription>{event.name}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : stage === 'form' ? (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{isManagingRsvp ? 'Manage RSVP' : 'RSVP to Attend'}</DialogTitle>
              <DialogDescription>
                {event.name}
                {canBringOthers ? (
                  <span className="mt-1 block text-xs">
                    Add a spouse/partner or guests below if they are joining you.
                  </span>
                ) : null}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {canBringOthers ? (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">
                      Who&apos;s attending with you?
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {maxCompanions == null
                        ? 'Add up to 1 spouse/partner and 4 guests (6 people total including you).'
                        : maxCompanions === 0
                          ? 'This event is near capacity, so additional guests cannot be added.'
                          : `Up to ${maxCompanions} additional ${maxCompanions === 1 ? 'person' : 'people'} can join you based on remaining seats.`}
                    </p>
                  </div>

                  <SpouseSection
                    linkedSpouse={linkedSpouse}
                    spouse={spouse}
                    errors={spouseErrors}
                    canEnable={spouseCanEnable}
                    onChange={handleSpouseChange}
                    onFieldBlur={handleSpouseFieldBlur}
                  />

                  <GuestSection
                    guests={guestState.guests}
                    guestCount={guestState.guestCount}
                    maxGuests={maxGuests}
                    errorsByGuestId={guestErrors}
                    onGuestCountChange={handleGuestCountChange}
                    onGuestChange={handleGuestChange}
                    onGuestRemove={handleGuestRemove}
                    onGuestFieldBlur={handleGuestFieldBlur}
                  />
                </>
              ) : null}

              {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
            </div>

            <DialogFooter className="gap-2 sm:justify-between">
              {isManagingRsvp ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={cancelMutation.isPending || submitMutation.isPending}
                  onClick={handleCancelRsvp}
                >
                  {cancelMutation.isPending ? 'Cancelling…' : 'Cancel RSVP'}
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Close
                </Button>
              )}
              <Button
                type="submit"
                disabled={submitMutation.isPending || cancelMutation.isPending}
                className="font-bold text-white sm:ml-auto"
                style={{ backgroundColor: 'var(--color-mr-teal-dark)' }}
              >
                {submitMutation.isPending
                  ? 'Submitting…'
                  : isManagingRsvp
                    ? 'Update RSVP'
                    : 'Submit RSVP'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {successMode === 'updated' ? 'Your RSVP has been updated' : "You're registered"}
              </DialogTitle>
              <DialogDescription>
                {successMode === 'updated' ? (
                  <>Your RSVP for {event.name} has been updated.</>
                ) : invitesSent ? (
                  <>
                    Your RSVP for {event.name} was successful. Invite emails will be sent to anyone
                    you opted in.
                  </>
                ) : (
                  <>Your RSVP for {event.name} was successful.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
